import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ServiceMenuListItem } from 'features/ServiceMenu/models';
import { selectServiceMenuSelectedItemIds, serviceMenuActions } from 'features/ServiceMenu/serviceMenuSlice';
import { mutliDragAwareReorder } from 'features/ServiceMenu/utils/dragutils/utils';
import { createContext, memo } from 'react';
import { DragDropContext, DraggingStyle, DragStart, DropResult, NotDraggingStyle } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import ServiceMenuRowList from '../ServiceMenuRowList';

export interface ServiceMenuContextProps {
  getListStyle: (isDraggingOver: boolean) => object;
  getItemStyle: (
    isDragging: boolean,
    draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
    isSelected?: boolean,
    isGhosting?: boolean
  ) => any;
  onSelectedRowChanged?: () => void;
  onRestoreServiceMenu?: (serviceMenuId: number) => void;
}

const getListStyle = (isDraggingOver: boolean) => ({
  background: isDraggingOver ? '#999' : 'lightgrey',
});

const getItemStyle = (isDragging: boolean, draggableStyle: any, isSelected?: boolean, isGhosting?: boolean) => {
  let backgroundColor = '#fff';

  if (isGhosting) {
    backgroundColor = '#FAFBFC';
  }

  if (isSelected) {
    backgroundColor = '#DEEBFF';
  }

  if (isDragging) {
    backgroundColor = '#ffff8d';
  }

  return {
    userSelect: 'none',
    background: backgroundColor,
    opacity: isGhosting ? '0' : '1',
    ...draggableStyle,
  };
};

export const COLUMN_WIDTH = [6, 34, 60];
export const ServiceMenuContext = createContext<ServiceMenuContextProps>({
  getListStyle: getListStyle,
  getItemStyle: getItemStyle,
});

interface ServiceMenuListTableProps {
  serviceMenuList: ServiceMenuListItem[];
  onRestoreServiceMenu: (serviceMenuId: number) => void;
  onSelectedRowChanged: () => void;
}

function ServiceMenuListTable({
  serviceMenuList,
  onRestoreServiceMenu,
  onSelectedRowChanged,
}: ServiceMenuListTableProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // state
  const selectedItemIds = useAppSelector(selectServiceMenuSelectedItemIds);

  function onDragStart(start: DragStart) {
    const id: string = start.draggableId;
    const selected = selectedItemIds.find((itemId: string): boolean => itemId === id);

    // if dragging an item that is not selected - unselect all items
    if (!selected) {
      dispatch(serviceMenuActions.unselectAll());
    }
    dispatch(serviceMenuActions.setDraggingItemId(id));
  }

  function onDragEnd(result: DropResult) {
    const destination = result.destination;
    const source = result.source;

    // nothing to do
    if (!destination || result.reason === 'CANCEL') {
      dispatch(serviceMenuActions.setDraggingItemId(undefined));
      return;
    }

    const droppableId = result.source.droppableId;
    const reorderData = mutliDragAwareReorder({
      data: serviceMenuList,
      selectedItemIds,
      source,
      destination,
      droppableId,
    });

    dispatch(
      serviceMenuActions.updateState({
        ...reorderData,
        selectedDroppableId: undefined,
        draggingItemId: undefined,
      })
    );
  }

  return (
    <ServiceMenuContext.Provider
      value={{
        getListStyle: getListStyle,
        getItemStyle: getItemStyle,
        onSelectedRowChanged: onSelectedRowChanged,
        onRestoreServiceMenu: onRestoreServiceMenu,
      }}
    >
      <div className="dan-sticky-table flex-grow-1 flex-shrink-1">
        <div className="dan-table">
          <div className="dan-thead">
            <div className="dan-tr">
              <div className="dan-th flex-grow-0 flex-shrink-0" style={{ width: COLUMN_WIDTH[0] + '%' }}>
                {t('ServiceMenuListPage.choice')}
              </div>
              <div className="dan-th flex-grow-0 flex-shrink-0" style={{ width: COLUMN_WIDTH[1] + '%' }}>
                {t('ServiceMenuListPage.menu_cd')}
              </div>
              <div className="dan-th flex-grow-0 flex-shrink-0" style={{ width: COLUMN_WIDTH[2] + '%' }}>
                {t('ServiceMenuListPage.menu_name')}
              </div>
            </div>
          </div>
          <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <ServiceMenuRowList data={serviceMenuList} />
          </DragDropContext>
        </div>
      </div>
    </ServiceMenuContext.Provider>
  );
}

export default memo(ServiceMenuListTable);
